import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DataTable, { DataTableColumn } from '../DataTable';
import {
  useGetAllArtifactsQuery,
  useGetAllModelsQuery,
  usePublishOrUnpublishArtifactMutation,
} from '../tools.service';
import { ArtifactType } from '../tools.types';
import { formatAiProviderName } from '../../../utils/aiProvider';
import { formatToolIdName, TOOL_IDS } from '../../../utils/toolId';
import { convertDateToNeedFormat } from '../../../utils/convertDateHandlers';
import { isFetchBaseQueryError } from '../../../utils/helpers';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useDebounce } from '../../../hooks/useDebounce';
import { usePagedList } from '../../../hooks/usePagedList';
import PaginationBar from '../../../UIComponents/PaginationBar';
import cls from './ArtifactsTab.module.scss';

/** One wide page is enough to pick a model in the filter */
const MODELS_FILTER_SIZE = 100;

const publishedFilterValues = {
  all: undefined,
  published: true,
  unpublished: false,
};

type PublishedFilter = keyof typeof publishedFilterValues;

const buildColumns = (
  publishHandler: (artifact: ArtifactType) => void,
  pendingArtifactId: string,
): DataTableColumn<ArtifactType>[] => [
  {
    key: 'url',
    title: 'Preview',
    width: 'minmax(90px, 0.5fr)',
    render: (artifact) => (
      <a href={artifact.url} target="_blank" rel="noopener noreferrer">
        Open
      </a>
    ),
  },
  {
    key: 'toolId',
    title: 'Tool',
    width: 'minmax(110px, 0.8fr)',
    render: (artifact) => formatToolIdName(artifact.toolId),
  },
  {
    key: 'prompt',
    title: 'Prompt',
    width: 'minmax(200px, 2fr)',
    render: (artifact) => <span title={artifact.prompt}>{artifact.prompt}</span>,
  },
  { key: 'modelName', title: 'Model', render: (artifact) => artifact.modelName || '-' },
  { key: 'styleName', title: 'Style', render: (artifact) => artifact.styleName || '-' },
  {
    key: 'providerId',
    title: 'Provider',
    width: 'minmax(110px, 0.8fr)',
    render: (artifact) => formatAiProviderName(artifact.providerId),
  },
  {
    key: 'createdAt',
    title: 'Created',
    width: 'minmax(150px, 0.8fr)',
    render: (artifact) =>
      artifact.createdAt
        ? convertDateToNeedFormat({ date: artifact.createdAt, format: 'datetime' })
        : '-',
  },
  {
    key: 'published',
    title: 'Explore',
    width: 'minmax(110px, 0.6fr)',
    render: (artifact) => (
      <span className={artifact.published ? cls.Published : cls.Unpublished}>
        {artifact.published ? 'Published' : 'Hidden'}
      </span>
    ),
  },
  {
    key: 'actions',
    title: '',
    width: 'minmax(160px, 0.7fr)',
    render: (artifact) => (
      <Button
        className={cls.ActionButton}
        onClick={() => publishHandler(artifact)}
        disabled={pendingArtifactId === artifact.id}
      >
        {artifact.published ? 'Unpublish' : 'Publish'}
      </Button>
    ),
  },
];

const ArtifactsTab = () => {
  const [consumerId, setConsumerId] = useState('');
  const [toolId, setToolId] = useState('');
  const [modelId, setModelId] = useState('');
  const [style, setStyle] = useState('');
  const [published, setPublished] = useState<PublishedFilter>('all');
  const [prompt, setPrompt] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const debouncedPrompt = useDebounce(prompt, 500);
  const debouncedStyle = useDebounce(style, 500);
  const debouncedConsumerId = useDebounce(consumerId, 500);
  const [pendingArtifactId, setPendingArtifactId] = useState('');
  const [publishOrUnpublishArtifact] = usePublishOrUnpublishArtifactMutation();
  useDocumentTitle('Artifacts');
  const filters = {
    consumerId: debouncedConsumerId || undefined,
    toolId: toolId || undefined,
    modelId: modelId || undefined,
    style: debouncedStyle || undefined,
    published: publishedFilterValues[published],
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    prompt: debouncedPrompt || undefined,
  };
  const { page, size, setPage, setSize } = usePagedList(filters);
  const { data, isLoading } = useGetAllArtifactsQuery({ page, size, ...filters });
  const { data: models } = useGetAllModelsQuery({ page: 0, size: MODELS_FILTER_SIZE });
  const publishHandler = async (artifact: ArtifactType) => {
    const switcher = artifact.published ? 'unpublish' : 'publish';
    setPendingArtifactId(artifact.id);
    try {
      await publishOrUnpublishArtifact({ artifactId: artifact.id, switcher }).unwrap();
      toast.success(artifact.published ? 'Removed from Explore' : 'Published to Explore');
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 403) toast.error('Access denied');
      else toast.error('Failed to change the artifact status');
    } finally {
      setPendingArtifactId('');
    }
  };
  const columns = buildColumns(publishHandler, pendingArtifactId);
  return (
    <div className={cls.Container}>
      <div className={cls.Filters}>
        <Form.Select
          value={toolId}
          onChange={(event) => setToolId(event.target.value)}
          className={cls.Filter}
          aria-label="Filter by tool"
        >
          <option value="">All tools</option>
          {TOOL_IDS.map((id) => (
            <option key={id} value={id}>
              {formatToolIdName(id)}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          value={published}
          onChange={(event) => setPublished(event.target.value as PublishedFilter)}
          className={cls.Filter}
          aria-label="Filter by explore status"
        >
          <option value="all">All artifacts</option>
          <option value="published">Published only</option>
          <option value="unpublished">Hidden only</option>
        </Form.Select>
        <Form.Select
          value={modelId}
          onChange={(event) => setModelId(event.target.value)}
          className={cls.Filter}
          aria-label="Filter by model"
        >
          <option value="">All models</option>
          {models?.models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </Form.Select>
        <Form.Control
          type="text"
          value={style}
          placeholder="Style name"
          onChange={(event) => setStyle(event.target.value)}
          className={cls.Filter}
        />
        <Form.Control
          type="text"
          value={consumerId}
          placeholder="Consumer id"
          onChange={(event) => setConsumerId(event.target.value)}
          className={cls.Filter}
        />
        <Form.Control
          type="text"
          value={prompt}
          placeholder="Search by prompt"
          onChange={(event) => setPrompt(event.target.value)}
          className={cls.Filter}
        />
        <div className={cls.DateRange}>
          <Form.Control
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(event) => setStartDate(event.target.value)}
            className={cls.DateFilter}
            aria-label="Created from"
          />
          <span>—</span>
          <Form.Control
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(event) => setEndDate(event.target.value)}
            className={cls.DateFilter}
            aria-label="Created to"
          />
        </div>
      </div>
      <DataTable
        columns={columns}
        items={data?.artifacts}
        isLoading={isLoading}
        emptyText="No artifacts found"
        rowKey={(artifact) => artifact.id}
        footer={
          data && (
            <PaginationBar
              page={page}
              size={size}
              countAll={data.countAll}
              onPageChange={setPage}
              onSizeChange={setSize}
              itemsName="artifacts"
            />
          )
        }
      />
    </div>
  );
};

export default ArtifactsTab;
